import { CheckCircle, Receipt, Person, Phone, Info, Event, Business, CalendarToday, AttachMoney, Description } from "@mui/icons-material";
import { Typography, Stack, Box, Grid, alpha, useTheme } from "@mui/material";
import { formatDateTimeWithLocale } from "../../../helpers";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";
import { LeadStatusRenderer } from "../../../components/LeadStatusRenderer/LeadStatusRenderer";


const getFieldIcon = (key: string): React.ElementType => {
    const iconMap: Record<string, React.ElementType> = {
        status: CheckCircle,
        booking_id: Receipt,
        booking_unique_id: Receipt,
        customer_name: Person,
        customer_mobile_no: Phone,
        customer_email: Info,
        venue_name: Business,
        shift_time: Event,
        event_time: Event,
        from_date: CalendarToday,
        to_date: CalendarToday,
        total_price: AttachMoney,
        food_menu: Description,
        created_by_name: Person,
        modified_by_name: Person,
        create_ts: CalendarToday,
        insert_ts: CalendarToday,
    };

    return iconMap[key.toLowerCase()] || Info;
};

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
    create_ts: "Created On",
    from_date: "Event Date",
    to_date: "Event End Date",
    total_price: "Total Amount",
    handled_by_employee: "Last Handled By Employee",
    created_by_name: "Created By",
    function_date: "Event Date",
    guest_budget: "Budget",
    name_of_guest: "Customer Name",
    contact_number_one: "Mobile No. ",
    preferred_venue: "Venue"
};

const formatFieldName = (key: string): string => {
    if (FIELD_LABEL_OVERRIDES[key]) {
        return FIELD_LABEL_OVERRIDES[key];
    }
    return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const formatFieldValue = (key: string, value: any, ftDate: any): string => {
    if (value === null || value === undefined || value === "") {
        return "--";
    }
    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }
    if (typeof value === "object") {
        return JSON.stringify(value);
    }

    const lowerKey = key.toLowerCase();
    if (lowerKey.includes("date") || lowerKey.includes("_ts")) {
        if (lowerKey.includes("_ts")) {
            return formatDateTimeWithLocale(String(value));
        } else {
            return ftDate(String(value)) || String(value);
        }
    }

    return String(value);
};

const shouldExcludeField = (key: string): boolean => {
    const lowerKey = key.toLowerCase();
    return lowerKey.includes("_uuid") || lowerKey.endsWith("uuid");
};

const SectionHeader = ({ title }: { title: string }) => {
    return (
        <Typography
            variant="subtitle2"
            sx={{
                fontWeight: 600,
                color: (theme) => theme.palette.primary.main,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontSize: "0.7rem",
                mb: 1,
            }}
        >
            {title}
        </Typography>
    );
};

const InfoItem: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string | number | null | undefined;
}> = ({ icon, label, value }) => {
    const theme = useTheme();
    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
            <Box sx={{ color: theme.palette.primary.main, display: "flex" }}>
                {icon}
            </Box>
            <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, minWidth: 90 }}
            >
                {label}:
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 500,
                    color: value
                        ? theme.palette.text.primary
                        : theme.palette.text.disabled,
                }}
            >
                {value || "—"}
            </Typography>
        </Stack>
    );
};

const BOOKING_DETAILS_SECTIONS = [
    {
        title: "Customer Information",
        fields: ["customer_name", "name_of_guest", "customer_email", "email", "customer_mobile_no", "contact_number_one", "create_ts"],
    },
    {
        title: "Event Information",
        fields: [
            "event_type",
            "event_time",
            "function_date",
            "venue_name",
            "preferred_venue"
        ],
    },
    {
        title: "Additional Information",
        fields: [
            "number_of_guests",
            "guest_budget",
            "handled_by_employee",
            "created_by_name",
        ],
    },
];



export const InventoryBookingDetails = ({ data }: { data: any }) => {
    const theme = useTheme();
    const { ftDate } = useTimezoneFormatter();

    return (
        <Box sx={{
            position: "relative", 
            border: "2px solid #dddddd",
            // background: "#f4f4f4",
            borderRadius: 1.75,
            mt: 2,
            pt: 2
        }}>
            <Grid container spacing={1} alignItems="stretch">
                {BOOKING_DETAILS_SECTIONS.map((section) => {
                    const visibleFields = section.fields.filter(
                        (key) => data?.[key] !== undefined && !shouldExcludeField(key),
                    );
                    if (visibleFields.length === 0) return null;

                    return (
                        <Grid item xs={12} md={4} key={section.title}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    // backgroundColor: alpha(theme.palette.grey[500], 0.04),
                                    // border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <SectionHeader title={section.title} />
                                <Box sx={{ flex: 1 }}>
                                    {visibleFields.map((key) => {
                                        const Icon = getFieldIcon(key);
                                        const formattedLabel = formatFieldName(key);
                                        const formattedValue = formatFieldValue(
                                            key,
                                            data[key],
                                            ftDate,
                                        );

                                        return (
                                            <InfoItem
                                                key={key}
                                                icon={<Icon sx={{ fontSize: 16 }} />}
                                                label={formattedLabel}
                                                value={formattedValue}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
            <Box sx={{
                position: 'absolute',
                top: -2,
                left: -1,
            }}>
                <LeadStatusRenderer
                    status={data?.status || ""}
                    actualStatus={data?.actual_status || ""}
                />
            </Box>
        </Box>
    )
}